<template>
  <span
    :class="
      cn(
        'tw:inline-flex tw:shrink-0 tw:select-none tw:items-center tw:justify-center tw:overflow-hidden tw:rounded-md tw:bg-gray-100 tw:font-medium tw:uppercase tw:text-muted',
        SIZE[size],
        $attrs.class as string | undefined
      )
    "
    v-bind="restAttrs">
    <img
      v-if="src"
      :src="src"
      :alt="alt ?? ''"
      :class="fit === 'contain' ? 'tw:size-full tw:object-contain' : 'tw:size-full tw:object-cover'" />
    <template v-else>
      <slot>{{ initials }}</slot>
    </template>
  </span>
</template>

<script setup lang="ts">
// LeadAvatar — mirrors the LEAD Design System <Avatar> component 1:1.
// Class strings and structure transcribed from
// .claude/skills/migrate-tailwind/design-source/components/Avatar/Avatar.tsx.
//
// See the source's doc block for why the small sizes exist for two specific hosts and why the
// type ratio is abandoned below 32px. Don't add new sizes without a specific host that needs one.
import { computed, useAttrs } from 'vue';
import { cn } from '@/lib/cn';

defineOptions({ inheritAttrs: false });

export type LeadAvatarSize = 'md' | 'sm' | 'xs' | '2xs';

withDefaults(
  defineProps<{
    size?: LeadAvatarSize;
    /** Image URL. Falls back to `initials` (or the default slot) when absent. */
    src?: string;
    /** Usually two letters. Rendered when there is no `src`. */
    initials?: string;
    /** Accessible name. Required when `src` is set. */
    alt?: string;
    /**
     * How the image sits inside the box. `cover` (DS default) fills and crops — right for portraits.
     * `contain` fits the whole image inside — right for company logos that already have padded whitespace.
     */
    fit?: 'cover' | 'contain';
  }>(),
  {
    size: 'md',
    src: undefined,
    initials: undefined,
    alt: undefined,
    fit: 'cover',
  }
);

const SIZE: Record<LeadAvatarSize, string> = {
  md: 'tw:size-10 tw:text-14',
  sm: 'tw:size-8 tw:text-[11px]',
  xs: 'tw:size-6 tw:rounded-sm! tw:text-[11px]',
  '2xs': 'tw:size-[18px] tw:rounded-sm! tw:text-10',
};

const attrs = useAttrs();
const restAttrs = computed(() => {
  const { class: _c, ...rest } = attrs;
  return rest;
});
</script>
